import React from "react";
import { Quote } from "lucide-react";

const BIO_IMG =
  "https://customer-assets.emergentagent.com/wingman/c7c109c9-9568-461b-a0d8-c29fcf0a280a/attachments/d58869eaac4644cc85365d85d60a6660_image.bin";

const MILESTONES = [
  { year: "2003", text: "Top 10 der ersten DSDS-Staffel (7. Platz) · Top-10-Single über mehrere Wochen · Top-20-Album · DVD-Produktion auf den Bahamas mit Frank Farian (Boney M., Milli Vanilli) · Cover und Stories in BRAVO und allen großen Tratsch-Magazinen" },
  { year: "2008", text: "Eigene Hits, ausverkaufte Tourneen, TV-Präsenz" },
  { year: "2015", text: "Rückkehr nach persönlicher Pause — neuer künstlerischer Reifegrad" },
  { year: "2018", text: "Top 10 bei The Voice of Brasil — internationale Bühne, brasilianisches Live-Publikum" },
  { year: "2024", text: "Promi Big Brother, virale Schlager-Comebacks" },
  { year: "2026", text: "Neue Single-Releases, Festival-Headliner-Slots" },
];

export default function Biography() {
  return (
    <section
      id="biografie"
      data-testid="biography-section"
      className="relative bg-[#0A0A0A] py-24 md:py-36 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          {/* Image side */}
          <div className="lg:col-span-5 lg:sticky lg:top-32">
            <div className="relative zoom-wrap" data-testid="biography-image-wrap">
              <img
                src={BIO_IMG}
                alt="Daniel Lopes — Red Carpet 2024"
                className="w-full h-[560px] md:h-[680px] object-cover"
              />
              <div className="absolute -bottom-4 -right-4 hidden md:block w-24 h-24 border border-[#D4AF37]" />
            </div>
            <div className="mt-6 flex items-center gap-3">
              <span className="gold-rule" />
              <span className="overline">Red Carpet · TV-Event · 2024</span>
            </div>
          </div>

          {/* Text side */}
          <div className="lg:col-span-7">
            <p className="overline" data-testid="biography-overline">
              Stimme · Gefühl · Persönlichkeit
            </p>
            <h2
              className="font-display mt-4 text-white leading-[1.05] tracking-tight"
              style={{ fontSize: "clamp(2.4rem, 5vw, 4.2rem)", fontWeight: 600 }}
              data-testid="biography-headline"
            >
              Eine Stimme,
              <br />
              <span className="italic text-[#D4AF37]">die bleibt.</span>
            </h2>

            <div className="mt-10 space-y-6 text-[#C9C9C9] font-body text-base md:text-lg leading-relaxed max-w-xl">
              <p data-testid="biography-paragraph-1">
                Bekannt aus der ersten Staffel von{" "}
                <span className="text-white">Deutschland sucht den Superstar</span>
                , wurde Daniel Lopes schnell zum Publikumsliebling. Mit seiner
                Hit-Single{" "}
                <span className="text-[#D4AF37]">„Shine On"</span> landete er
                direkt in den{" "}
                <span className="text-[#D4AF37]">Top 10</span>. Es folgten
                TV-Erfolge, eine{" "}
                <span className="text-[#D4AF37]">The Voice Brasil</span>-Teilnahme
                und der Schritt zum eigenen, deutschsprachigen Sound.
              </p>
              <p data-testid="biography-paragraph-2">
                Ob gefühlvoller Pop, tanzbarer Beat oder emotionale Ballade —
                Daniel steht für echten, melodischen Deutschpop mit Charakter.
                Sein Duett{" "}
                <span className="text-[#D4AF37]">„Ich kann für nichts garantieren"</span>{" "}
                mit{" "}
                <span className="text-white">Pia Sophie</span> erreichte{" "}
                <span className="text-[#D4AF37]">Platz 1</span> der konservativen
                Airplay-Charts.
              </p>

              {/* Highlight: Aktuelle Single */}
              <div
                data-testid="biography-current-single"
                className="relative mt-2 p-6 md:p-7 border border-[#D4AF37]/40 bg-[#D4AF37]/[0.04]"
              >
                <div className="flex items-center gap-3 mb-3">
                  <span className="inline-flex items-center gap-2 px-2.5 py-1 bg-[#D4AF37] text-[#0A0A0A] text-[10px] tracking-[0.28em] uppercase font-medium">
                    Aktuell · 2026
                  </span>
                  <span className="text-[10px] tracking-[0.24em] uppercase text-[#D4AF37]">
                    Neue Single
                  </span>
                </div>
                <p className="font-display text-2xl md:text-3xl text-white leading-tight mb-2">
                  „Es geht dir gut"
                </p>
                <p className="font-body text-[#C9C9C9] text-base leading-relaxed">
                  Ein Song voller Emotionen und moderner Beats — ab sofort
                  überall erhältlich.
                </p>
                <a
                  href="#musik"
                  data-testid="biography-current-single-link"
                  className="inline-flex items-center gap-2 mt-4 text-xs tracking-[0.2em] uppercase text-[#D4AF37] hover:text-[#F3E5AB] transition-colors"
                >
                  Jetzt anhören →
                </a>
              </div>

              <p data-testid="biography-paragraph-3">
                Daniel arbeitet mit frischem Team an neuen Songs — mit
                Leidenschaft, Tiefe und viel Herz.
              </p>
            </div>

            {/* Pull-quote */}
            <div
              className="mt-12 border-l-2 border-[#D4AF37] pl-6 py-2"
              data-testid="biography-quote"
            >
              <Quote
                size={22}
                strokeWidth={1}
                className="text-[#D4AF37] mb-3"
              />
              <p className="font-display italic text-xl md:text-2xl text-white leading-snug max-w-lg">
                „Daniel Lopes — eine Stimme, die bleibt."
              </p>
              <p className="mt-3 text-xs tracking-[0.22em] uppercase text-[#A3A3A3]">
                Premium Deutschpop · Schlager · Live
              </p>
            </div>

            {/* Timeline */}
            <div className="mt-14" data-testid="biography-timeline">
              <p className="overline mb-6">Karriere · Highlights</p>
              <ul className="space-y-5 border-t border-white/5">
                {MILESTONES.map((m) => (
                  <li
                    key={m.year}
                    className="grid grid-cols-12 gap-4 pt-5 border-b border-white/5 pb-5 group"
                  >
                    <span className="col-span-3 md:col-span-2 font-display text-[#D4AF37] text-xl md:text-2xl">
                      {m.year}
                    </span>
                    <span className="col-span-9 md:col-span-10 text-[#C9C9C9] font-body text-base md:text-lg group-hover:text-white transition-colors">
                      {m.text}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
